import HCaptcha from '@hcaptcha/react-hcaptcha';
import {useEffect, useRef, useState} from 'react';
import {FormProvider} from 'react-hook-form';
import {connect} from 'react-redux';
import {Link, useNavigate} from 'react-router';

import {PasswordWithEmailT} from '@/lib/types/authentication.types';

import {ThemeChanger} from '@/lib/redux/action';

import {FormIds} from '@/lib/forms/form-ids';
import {signInFormFields, signInFormSteps} from '@/lib/forms/sign-in-form-config';
import {getBaseURL} from '@/lib/helpers/urlHelpers';

import {useAuth} from '@/providers/components/authProvider';
import {useAppForm} from '@/providers/components/formProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import Button from '@/components/Button';
import {LocalStorageBackup} from '@/components/common/switcher/switcherdata/switcherdata';
import FormFields from '@/components/form/FormFields';
import FormGlobalError from '@/components/form/FormGlobalError';
import FormSubmitButton from '@/components/form/FormSubmitButton';

type SignInPagePropsT = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ThemeChanger: any;
};

function SignInPage({ThemeChanger}: SignInPagePropsT) {
  const navigate = useNavigate();
  const {toast} = useNotifications();
  const {handleEmailAndPasswordSignIn, handleGoogleAuthentication, handleFacebookAuthentication} = useAuth();
  const {handleResendEmailVerification} = useAuth();
  // State
  const [captchaToken, setCaptchaToken] = useState<string | undefined>();

  // Refs
  const captchaRef = useRef<HCaptcha | null>(null);

  // Create the form in the form provider
  const {formConfig, setGlobalError, setLoading} = useAppForm<PasswordWithEmailT>({
    formId: FormIds.SIGN_IN,
    config: {
      fields: signInFormFields,
      steps: signInFormSteps,
    },
  });

  // React Hook Form Config
  const {
    handleSubmit,
    trigger,
    formState: {errors},
    reset,
    getValues,
  } = formConfig;

  useEffect(() => {
    LocalStorageBackup(ThemeChanger);
  }, [ThemeChanger]);

  // Submit handler
  const onSubmit = async (credentials: PasswordWithEmailT) => {
    if (Object.keys(errors).length) {
      setGlobalError('Something went wrong. Please check the form for errors');
      trigger();
      return;
    }

    if (!captchaToken) {
      setGlobalError('Please complete the CAPTCHA verification');
      return;
    }

    try {
      setLoading(true);
      await handleEmailAndPasswordSignIn({...credentials, captchaToken});
      navigate('/auth/callback');
    } catch (error) {
      setGlobalError((error as Error).message ?? 'Something went wrong. Please try again later.');
      reset({email: credentials.email});
    } finally {
      captchaRef.current?.resetCaptcha();
      setLoading(false);
    }
  };

  const handleGoogleAuth = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      reset();
      setLoading(true);
      await handleGoogleAuthentication();
    } catch (error) {
      setGlobalError((error as Error).message ?? 'Something went wrong. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleFacebookAuth = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      reset();
      setLoading(true);
      await handleFacebookAuthentication();
    } catch (error) {
      setGlobalError((error as Error).message ?? 'Something went wrong. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasswordSelect = async () => {
    const isValid = await trigger('email');

    if (!isValid) {
      toast.error({
        title: 'Hey wait a minute...',
        message: 'You must enter your email address in the form first.',
        icon: 'alarm-warning-line',
      });
      return;
    }

    if (!captchaToken) {
      setGlobalError('Please complete the CAPTCHA verification');
      return;
    } else {
      setGlobalError('');
    }

    const email = getValues('email');

    try {
      setLoading(true);
      await handleResendEmailVerification(email, captchaToken);
      toast.success({
        title: 'Success',
        message: 'Password reset email sent successfully',
      });
      reset();
    } catch {
      toast.error({
        title: 'Error',
        message: 'An error occurred while sending the password reset email',
      });
    } finally {
      captchaRef.current?.resetCaptcha();
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="authentication authentication-basic h-full items-center text-defaultsize text-defaulttextcolor">
        <div className="mx-auto max-w-lg">
          <div className="my-[2.5rem] flex justify-center">
            <Link to={getBaseURL()} tabIndex={-1} aria-label="Go to homepage">
              <img src={'/images/logo/logo.png'} alt="logo" className="desktop-logo max-w-[150px]" />
              <img src={'/images/logo/logo.png'} alt="logo" className="desktop-dark max-w-[150px]" />
            </Link>
          </div>

          <div className="box">
            <div className="box-body !p-[3rem]">
              <h1 className="h3 mb-2 text-center font-semibold md:h1" id="signin-heading">
                Sign In
              </h1>
              <small className="mb-5 block text-center text-gray-800/70 dark:text-white/40" id="signin-terms-text">
                By signing in with a social login or manually
                <br />
                you are agreeing to our{' '}
                <Link
                  to="https://tos.tnc.exchange/"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Terms and Conditions (opens in new tab)"
                >
                  <u>Terms &amp; Conditions</u>
                </Link>
                <span className="mx-1">and</span>
                <Link
                  to="https://pp.tnc.exchange/"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Privacy Policy (opens in new tab)"
                >
                  <u>Privacy Policy</u>
                </Link>
                .
              </small>

              <div className="flex flex-col gap-2 text-center" role="group" aria-labelledby="social-login-heading">
                <span id="social-login-heading" className="sr-only">
                  Social Login Options
                </span>
                <Button onClick={handleFacebookAuth} color="facebook" aria-label="Sign in with Facebook">
                  Sign with Facebook
                </Button>
                <Button onClick={handleGoogleAuth} color="google" aria-label="Sign in with Google">
                  Sign with Google
                </Button>
              </div>

              <div className="authentication-barrier my-4 text-center">
                <span className="text-xs text-gray-800/80 dark:text-white/40">Or Sign In With</span>
              </div>

              <FormProvider {...formConfig}>
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className="flex flex-col gap-4"
                  aria-labelledby="signin-heading"
                  role="form"
                >
                  <FormGlobalError formId={FormIds.SIGN_IN} />
                  <FormFields formId={FormIds.SIGN_IN} />
                  <div className="flex justify-center">
                    <HCaptcha
                      ref={captchaRef}
                      sitekey={import.meta.env.VITE_HCAPTCHA_SITE_KEY}
                      onVerify={token => {
                        setCaptchaToken(token);
                      }}
                      onExpire={() => {
                        captchaRef.current?.resetCaptcha();
                        setCaptchaToken(undefined);
                      }}
                    />
                  </div>
                  <FormSubmitButton formId={FormIds.SIGN_IN} label="Sign In" showStepCount={false} />
                  <Button
                    aria-label="Forget password"
                    onClick={handleResetPasswordSelect}
                    variant="basic"
                    width="full"
                    color="muted"
                  >
                    Forget password?
                  </Button>
                </form>
              </FormProvider>

              <div className="text-center">
                <p className="mt-4 text-[0.75rem] text-[#8c9097] dark:text-white/50">
                  Don&apos;t have an account?{' '}
                  <Link to={`${getBaseURL()}/sign-up`} className="text-primary" aria-label="Sign up for a new account">
                    Sign Up
                  </Link>
                </p>
              </div>
            </div>
          </div>
          <div className="mb-4 flex items-center justify-center gap-1">
            <Link
              to="https://tos.tnc.exchange/"
              className="text-success"
              target="_blank"
              rel="noreferrer"
              aria-label="Terms and Conditions (opens in new tab)"
            >
              <u>Terms &amp; Conditions</u>
            </Link>
            <span aria-hidden="true">|</span>
            <Link
              to="https://pp.tnc.exchange/"
              className="text-success"
              target="_blank"
              rel="noreferrer"
              aria-label="Privacy Policy (opens in new tab)"
            >
              <u>Privacy Policy</u>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapStateToProps = (state: any) => ({
  local_variable: state.theme,
});

export default connect(mapStateToProps, {ThemeChanger})(SignInPage);
